import React, { useMemo, useRef, useState } from 'react';
import {
    Button,
    CircularProgress,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
} from '@mui/material';
import {
    Download as DownloadIcon,
    Image as ImageIcon,
    PictureAsPdf as PdfIcon,
    TableChart as CsvIcon,
    DashboardCustomize as EditLayoutIcon,
    Check as DoneIcon,
} from '@mui/icons-material';
import { useCommonTranslation } from 'c-translation';
import DashboardWidget from '../DashboardWidget';
import DashboardGrid from '../DashboardGrid';
import { gridReadingOrder } from '../dashboardGridLayout';
import { DashboardReportProvider } from '../useDashboardReport';
import {
    DashboardExportProvider,
    combineCsvSections,
    useDashboardExport,
} from '../DashboardExportContext';
import {
    EXPORT_IGNORE_ATTR,
    downloadCsv,
    downloadElementImage,
    exportFileName,
} from '../dashboardExport';
import type { DashboardConfig, DashboardLayoutConfig } from '../dashboardConfig';
import { calculatedFieldsForSource, normalizeDashboardConfig } from '../dashboardConfig';

// Download the whole dashboard — every widget, not the page's tabs — as
// an image or PDF, or every widget's data in one CSV.
const DashboardDownloadMenu: React.FC<{
    target: React.RefObject<HTMLDivElement>;
    widgetOrder: string[];
    name: string;
}> = ({ target, widgetOrder, name }) => {
    const t = useCommonTranslation();
    const exporter = useDashboardExport();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [busy, setBusy] = useState(false);
    const fileName = exportFileName(name);
    const image = (kind: 'png' | 'pdf') => {
        setAnchor(null);
        if (!target.current) return;
        setBusy(true);
        downloadElementImage(target.current, fileName, kind)
            .finally(() => setBusy(false))
            .catch(() => undefined);
    };
    const data = () => {
        setAnchor(null);
        const sections = exporter?.collect(widgetOrder) ?? [];
        if (sections.length) downloadCsv(combineCsvSections(sections), fileName);
    };
    return (
        <>
            <Button
                size="small"
                variant="outlined"
                disabled={busy}
                startIcon={busy ? <CircularProgress size={14} /> : <DownloadIcon />}
                onClick={e => setAnchor(e.currentTarget)}
            >
                {t('Pages.DashboardHome.custom.downloadDashboard')}
            </Button>
            <Menu anchorEl={anchor} open={anchor != null} onClose={() => setAnchor(null)}>
                <MenuItem onClick={() => image('png')}>
                    <ListItemIcon>
                        <ImageIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('Pages.DashboardHome.custom.downloadPng')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => image('pdf')}>
                    <ListItemIcon>
                        <PdfIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('Pages.DashboardHome.custom.downloadPdf')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={data}>
                    <ListItemIcon>
                        <CsvIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('Pages.DashboardHome.custom.downloadAllCsv')}</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
};

type FirstTabProps = {
    // The dashboard this tab renders: the built-in default set for the YTD Aggregate
    // tab, or the user's saved layout for the Custom tab.
    config?: DashboardConfig;
    // Given, the widgets can be dragged and resized; called with the new layout after each
    // move. The built-in dashboard leaves it out, so it can't be rearranged.
    onLayoutChange?: (layout: DashboardLayoutConfig) => void;
};

const FirstTab = ({ config: rawConfig, onLayoutChange }: FirstTabProps) => {
    const t = useCommonTranslation();
    const config = useMemo(() => normalizeDashboardConfig(rawConfig), [rawConfig]);
    const pageRef = useRef<HTMLDivElement>(null);
    const widgetOrder = useMemo(
        () => gridReadingOrder(config.layout.items).map(item => item.widgetId),
        [config],
    );
    const [editingLayout, setEditingLayout] = useState(false);
    // Each widget gets the calculated fields written against the source it reads.
    const calcsByWidget = useMemo(
        () =>
            Object.fromEntries(
                config.widgets.map(w => [
                    w.id,
                    calculatedFieldsForSource(config.calculatedFields, w.datasourceId),
                ]),
            ),
        [config],
    );

    return (
        <DashboardReportProvider>
            <DashboardExportProvider>
                <Stack spacing={2} ref={pageRef}>
                    <Stack
                        direction="row"
                        justifyContent="flex-end"
                        gap={1}
                        {...{ [EXPORT_IGNORE_ATTR]: true }}
                    >
                        {onLayoutChange && (
                            <Button
                                size="small"
                                variant={editingLayout ? 'contained' : 'outlined'}
                                startIcon={editingLayout ? <DoneIcon /> : <EditLayoutIcon />}
                                onClick={() => setEditingLayout(current => !current)}
                            >
                                {t(
                                    editingLayout
                                        ? 'Pages.DashboardHome.custom.layout.done'
                                        : 'Pages.DashboardHome.custom.layout.edit',
                                )}
                            </Button>
                        )}
                        <DashboardDownloadMenu
                            target={pageRef}
                            widgetOrder={widgetOrder}
                            name={config.name ?? t('Pages.DashboardHome.metaTitle')}
                        />
                    </Stack>
                    <DashboardGrid
                        items={config.layout.items}
                        editing={editingLayout}
                        onChange={items => onLayoutChange?.({ items })}
                        renderItem={(item, widthPct) => {
                            const widget = config.widgets.find(w => w.id === item.widgetId);
                            return widget ? (
                                <DashboardWidget
                                    config={widget}
                                    globalFilters={config.globalFilters}
                                    widthPct={widthPct}
                                    calculatedFields={calcsByWidget[widget.id]}
                                    fill
                                    editing={editingLayout}
                                />
                            ) : null;
                        }}
                    />
                </Stack>
            </DashboardExportProvider>
        </DashboardReportProvider>
    );
};

export default FirstTab;
